import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { SectionHeading, Eyebrow } from "@/components/site/Sections";
import { FieldServiceCard, CategoryTile } from "@/components/field/FieldServiceCard";
import { ServiceAreaMap, SpecializedSection } from "@/components/field/FieldSections";
import { getFieldServices } from "@/lib/api";
import { FIELD_CATEGORIES, TAG_FILTERS } from "@/data/fieldServices";

export const FieldCatalog = ({ services }) => {
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [cat, setCat] = useState("");
  const rows = useMemo(() => services.filter((s) =>
    (!tag || (s.tags || []).includes(tag) || (tag === "residential" && (s.tags || []).includes("commercial"))) &&
    (!cat || s.category === cat) &&
    (!q || `${s.name} ${s.description} ${(s.includes || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()))), [services, q, tag, cat]);
  return (
    <div data-testid="fs-catalog">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services (e.g. drive-by, winterization, draw)…" data-testid="fs-search"
            className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm focus:border-gold focus:outline-none" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="fs-category-filter" className="rounded-full border border-slate-300 bg-white px-4 py-3 text-sm">
          <option value="">All categories</option>
          {FIELD_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {[["", "All"], ...TAG_FILTERS].map(([k, label]) => (
          <button key={k || "all"} type="button" onClick={() => setTag(k)} data-testid={`fs-tag-${k || "all"}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tag === k ? "bg-[#0B132B] text-white" : "bg-white text-slate-600 hover:text-[#0B132B]"}`}>{label}</button>
        ))}
      </div>
      <p className="mt-6 text-sm text-slate-500" data-testid="fs-count">{rows.length} services</p>
      <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{rows.map((s) => <FieldServiceCard key={s.id} s={s} />)}</div>
    </div>
  );
};

export default function FieldServices() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    getFieldServices().then(setServices).catch(() => setServices([]));
  }, []);
  return (
    <div data-testid="field-services-page">
      <section className="border-b border-slate-200 bg-[#F5F3EC]">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
          <Eyebrow>Property Inspections & Field Services</Eyebrow>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-tight text-[#0B132B] sm:text-5xl">Inspections, field services and property preservation across Pennsylvania.</h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Backed by 9+ years of hands-on experience in real estate acquisitions, fix-and-flip projects, construction coordination, property evaluations and inspections. Reliable documentation for investors, lenders, property managers and commercial clients.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CtaButton to="/request-inspection" size="lg" data-testid="fs-hero-request">Request an Inspection</CtaButton>
            <CtaButton href="#catalog" variant="slate" size="lg" data-testid="fs-hero-explore">Explore Our Services</CtaButton>
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading eyebrow="Categories" title="Browse by service category" />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{FIELD_CATEGORIES.map((c) => <CategoryTile key={c.id} c={c} />)}</div>
        </div>
      </section>
      <section id="catalog" className="bg-[#F5F3EC] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading eyebrow="Service catalog" title="Find the right service" subtitle='Prices show "Starting at" only when a price has been set. Everything else is quoted after review.' />
          <div className="mt-10"><FieldCatalog services={services} /></div>
        </div>
      </section>
      <SpecializedSection />
      <ServiceAreaMap />
    </div>
  );
}
